import { Play, Plus, Check, Clapperboard, Flame, Star, Heart } from "lucide-react";
import { fileUrl } from "@/lib/api";

export default function Hero({ show, onTrailer, watchlist, onWatchlist, liked, onLike }) {
  if (!show) {
    return <section id="explore" data-testid="hero-section" className="min-h-screen bg-ink-950" />;
  }
  const inList = watchlist.has(show.slug);
  return (
    <section id="explore" data-testid="hero-section" className="relative flex min-h-screen items-end overflow-hidden">
      <div className="absolute inset-0">
        {show.poster_url ? (
          <img
            src={fileUrl(show.banner_url || show.poster_url)}
            alt={show.title}
            className="h-full w-full animate-kenburns object-cover object-top"
          />
        ) : (
          <div className="h-full w-full bg-ink-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/75 to-ink-950/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-transparent to-ink-950/70" />
      </div>
      <div className="absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-gold-500/10 blur-[140px]" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-40 lg:px-8 lg:pb-28">
        <div className="max-w-2xl">
          <div className="flex flex-wrap items-center gap-3 animate-fade-up">
            <span
              data-testid="hero-badge-launch"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold-600 to-gold-400 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-ink-950 shadow-[0_0_24px_rgba(212,175,55,0.35)]"
            >
              <Star className="h-3.5 w-3.5" /> Launching with {show.title}
            </span>
            <span
              data-testid="hero-badge-trending"
              className="inline-flex items-center gap-2 rounded-full border border-gold-500/50 bg-ink-950/60 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-gold-400 backdrop-blur"
            >
              <Flame className="h-3.5 w-3.5" /> Trending #1
            </span>
          </div>

          <p className="mt-9 animate-fade-up anim-delay-1 text-[11px] font-semibold uppercase tracking-[0.5em] text-gold-500/90">
            {show.category}
          </p>
          <h1 className="mt-3 animate-fade-up anim-delay-1 font-display text-6xl font-black leading-none sm:text-7xl lg:text-8xl">
            <span className="gold-text drop-shadow-[0_2px_18px_rgba(212,175,55,0.25)]">{show.title}</span>
          </h1>
          <p className="mt-6 max-w-xl animate-fade-up anim-delay-2 text-base leading-relaxed text-stone-300 sm:text-lg">
            {show.description}
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 animate-fade-up anim-delay-2 text-[12px] uppercase tracking-[0.22em] text-stone-400">
            <span>Fewbarsh Original</span>
            <span className="h-1 w-1 rounded-full bg-gold-500" />
            <span>{show.status === "live" ? "Streaming 2026" : "Coming Soon"}</span>
            <span className="h-1 w-1 rounded-full bg-gold-500" />
            <span>Only on VIEWNA</span>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4 animate-fade-up anim-delay-3">
            <button
              data-testid="hero-play-button"
              onClick={() => onTrailer(show)}
              className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-gold-600 via-gold-400 to-gold-600 bg-[length:200%_100%] px-9 py-4 text-[13px] font-bold uppercase tracking-[0.18em] text-ink-950 transition-all duration-500 hover:bg-right hover:shadow-[0_0_36px_rgba(212,175,55,0.45)]"
            >
              <Play className="h-4 w-4 fill-current" /> Play
            </button>
            <button
              data-testid="hero-watch-trailer-button"
              onClick={() => onTrailer(show)}
              className="inline-flex items-center gap-3 rounded-full border border-gold-500/50 bg-ink-950/50 px-9 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-gold-300 backdrop-blur transition-all duration-300 hover:border-gold-400 hover:bg-gold-500/10"
            >
              <Clapperboard className="h-4 w-4" /> Watch Trailer
            </button>
            <button
              data-testid="hero-watchlist-button"
              onClick={() => onWatchlist(show.slug, show.title)}
              className="inline-flex items-center gap-3 rounded-full border border-stone-600/60 px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-stone-200 transition-all duration-300 hover:border-gold-500/60 hover:text-gold-300"
            >
              {inList ? <Check className="h-4 w-4 text-gold-400" /> : <Plus className="h-4 w-4" />}
              {inList ? "In Watchlist" : "Add to Watchlist"}
            </button>
            <button
              data-testid="hero-like-button"
              onClick={() => onLike(show.slug, show.title)}
              className="inline-flex items-center gap-3 rounded-full border border-stone-600/60 px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-stone-200 transition-all duration-300 hover:border-red-500/60 hover:text-red-300"
            >
              <Heart className={`h-4 w-4 ${liked.has(show.slug) ? "fill-red-500 text-red-400" : ""}`} />
              {liked.has(show.slug) ? "Liked" : "Like"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
